import { Box, IconButton, Typography, styled } from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ButtonBase from 'design-system/button'
import React, { useRef, useState } from 'react'

/** Tipos aceitos pelo backend (whitelist espelhada do storage) */
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

/** Limite antes do redimensionamento; o backend também valida */
const MAX_FILE_SIZE = 5 * 1024 * 1024

const Preview = styled(Box)(({ theme }) => ({
  width: 220,
  height: 120,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper
}))

interface LogoUploadProps {
  /** URL já persistida (modo edição) ou preview local (modo cadastro) */
  value?: string | null
  disabled?: boolean
  loading?: boolean
  onSelect: (file: File) => void
  onRemove: () => void
}

export const LogoUpload = ({
  value,
  disabled,
  loading,
  onSelect,
  onRemove
}: LogoUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string>('')

  const handleRemove = () => {
    setError('')
    onRemove()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    // Permite reselecionar o mesmo arquivo depois de remover
    event.target.value = ''

    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Formato inválido. Use PNG, JPEG ou WebP.')
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Imagem muito grande. O limite é 5 MB.')
      return
    }

    setError('')
    onSelect(file)
  }

  return (
    <Box>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        Logotipo (opcional)
      </Typography>

      <Box display='flex' alignItems='center' gap={3}>
        <input
          type='file'
          accept={ACCEPTED_TYPES.join(',')}
          ref={inputRef}
          style={{ display: 'none' }}
          onChange={handleChange}
        />

        <Preview>
          {value ? (
            <img
              src={value}
              alt='Logotipo do estabelecimento'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          ) : (
            <AddPhotoAlternateIcon color='disabled' fontSize='large' />
          )}
        </Preview>

        <Box display='flex' flexDirection='column' gap={1}>
          <ButtonBase
            variant='outlined'
            size='small'
            disabled={disabled || loading}
            loading={loading}
            onClick={() => inputRef.current?.click()}
          >
            {value ? 'Alterar' : 'Selecionar'}
          </ButtonBase>

          {value && (
            <IconButton
              size='small'
              color='error'
              disabled={disabled || loading}
              onClick={handleRemove}
              aria-label='Remover logotipo'
              sx={{ alignSelf: 'flex-start' }}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          )}
        </Box>
      </Box>

      {error && (
        <Typography variant='caption' color='error' display='block' mt={0.5}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

export default LogoUpload
